import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { OrderItem, BuyerStoreOrder } from '@/types/models/Order';
import { supabase } from '@/lib/supabase';
import { paginateSupabaseQuery } from '@/utils/supabasePagination';
import { CustomerOrderService } from '@/services';
import { logger } from '@/lib/logger';
// Public store item shape (mirrors public.public_store_items view)
export interface PublicStoreItemView {
  id: string;
  store_id: string;
  language_locale: string | null;
  name: string;
  unit: string;
  divisible_unit: boolean;
  name_locale: string | null;
  description: string | null;
  keywords: string | null;
  categories: string[] | null;
}

// Lightweight store details coming from public_stores / explore list
export interface PublicStoreView {
  id: string;
  name: string;
  name_locale?: string | null;
  address?: string | null;
  address_locale?: string | null;
  phone?: string | null;
  totalCustomers?: number | null;
  totalItems?: number | null;
  language_locale?: string | null;
  online_price_enabled?: boolean | null;
}

export interface BuyerStoreDetails {
  meta?: PublicStoreView;
  // Items from public.public_store_items for this store
  items: PublicStoreItemView[];
  // Invoice count (all time)
  invoiceCount: number;
  // Total outstanding dues
  totalDues: number;
  // Recently bought item IDs (for "Recently Bought" tab)
  recentlyBoughtItemIds: string[];
  // Store-level refresh flags
  isRefreshing: boolean;
  lastRefreshedAt: string | null;
  // Error state
  error: string | null;
}

export interface BuyerState {
  // Current store buyer is viewing in buyer flow
  activeStoreId: string | null;
  // Per-store details (meta, items, stats)
  storesById: Record<string, BuyerStoreDetails>;
  // Orders by store ID (cart/order state)
  ordersByStoreId: Record<string, BuyerStoreOrder>;
  // Stores this buyer explores/shops from (used in explore/browse flows)
  exploredStores: PublicStoreView[];
}

const createEmptyStoreDetails = (): BuyerStoreDetails => {
  return {
    items: [],
    invoiceCount: 0,
    totalDues: 0,
    recentlyBoughtItemIds: [],
    isRefreshing: false,
    lastRefreshedAt: null,
    error: null,
  };
};

const initialState: BuyerState = {
  activeStoreId: null,
  storesById: {},
  ordersByStoreId: {},
  exploredStores: [],
};

// Async thunk to fetch connected stores for a buyer
export const fetchBuyerConnectedStores = createAsyncThunk(
  'buyer/fetchConnectedStores',
  async (buyerPhone: string, { rejectWithValue }) => {
    try {
      // Fetch store_customers to get connected store IDs
      const { data: storeCustomers, error: scError } = await supabase
        .from('store_customers')
        .select('store_id')
        .eq('customer_phone', buyerPhone);
      
      if (scError) throw scError;

      const storeIds = (storeCustomers || []).map((sc: any) => sc.store_id);
      if (storeIds.length === 0) {
        return { stores: [], storeIds: [] };
      }

      // Fetch stores from public_stores view and filter for enabled stores
      const { data: storesData, error: storesError } = await supabase
        .from('public_stores')
        .select('id, name, name_locale, address, address_locale, phone, total_items, total_customers, online_store_enabled, language_locale')
        .in('id', storeIds)
        .eq('online_store_enabled', true)
        .order('name', { ascending: true });
      
      if (storesError) throw storesError;

      const safeStores = storesData || [];
      
      // Transform to PublicStoreView format
      const stores: PublicStoreView[] = safeStores.map((s: any) => ({
        id: s.id,
        name: s.name,
        name_locale: s.name_locale,
        address: s.address,
        address_locale: s.address_locale,
        phone: s.phone,
        totalCustomers: s.total_customers,
        totalItems: s.total_items,
        language_locale: s.language_locale,
      }));

      return { stores };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch stores');
    }
  }
);

// Async thunk to fetch all store details (meta, items, stats)
export const fetchBuyerStoreDetails = createAsyncThunk(
  'buyer/fetchStoreDetails',
  async (
    { storeId, buyerPhone }: { storeId: string; buyerPhone?: string },
    { rejectWithValue },
  ) => {
    try {
      // Fetch store meta
      const { data: storeData, error: storeError } = await supabase
        .from('public_stores')
        .select('id, name, name_locale, address, address_locale, phone, total_customers, total_items, online_store_enabled, online_price_enabled, language_locale')
        .eq('id', storeId)
        .eq('online_store_enabled', true)
        .single();

      if (storeError) throw storeError;

      if (!storeData || !storeData.online_store_enabled) {
        return rejectWithValue('This store is not available online');
      }

      const store: PublicStoreView = {
        id: storeData.id,
        name: storeData.name,
        name_locale: storeData.name_locale,
        address: storeData.address,
        address_locale: storeData.address_locale,
        phone: storeData.phone,
        totalCustomers: storeData.total_customers,
        totalItems: storeData.total_items,
        language_locale: storeData.language_locale,
        online_price_enabled: storeData.online_price_enabled,
      };

      // Fetch store items
      const allItems = await paginateSupabaseQuery(
        (from, to) =>
          supabase
            .from('public_store_items')
            .select('*')
            .eq('store_id', storeId)
            .range(from, to),
        {
          logContext: `fetching public store items for store ${storeId}`,
        },
      );

      const safeItems = (allItems || []) as PublicStoreItemView[];

      // Fetch stats if buyer phone is provided
      let invoiceCount = 0;
      let totalDues = 0;
      let recentlyBoughtItemIds: string[] = [];

      if (buyerPhone) {
        // Optimized: Fetch invoice count and total dues in parallel
        const [countResult, balanceResult] = await Promise.all([
          // Fetch total invoice count (all time) - optimized count query, no data transfer
          supabase
            .from('invoices')
            .select('*', { count: 'exact', head: true })
            .eq('store_id', storeId)
            .eq('customer_phone', buyerPhone)
            .eq('is_manual_due', false)
            .eq('status', 'Completed'),
          
          // Fetch total dues - optimized single row query
          supabase
            .from('store_customer_balances')
            .select('balance')
            .eq('store_id', storeId)
            .eq('customer_phone', buyerPhone)
            .maybeSingle(), // Use maybeSingle instead of single to avoid error if no row
        ]);

        // Process invoice count
        if (countResult.error) {
          logger.error('Error fetching invoice count', { error: countResult.error, component: 'buyerSlice' });
          throw countResult.error;
        }
        invoiceCount = countResult.count ?? 0;

        // Process total dues
        if (balanceResult.error && balanceResult.error.code !== 'PGRST116') {
          logger.error('Error fetching total dues', { error: balanceResult.error, component: 'buyerSlice' });
          throw balanceResult.error;
        }
        totalDues = balanceResult.data?.balance && balanceResult.data.balance > 0 
          ? balanceResult.data.balance 
          : 0;

        // Fetch only last 20 invoices for recently bought items (only if we have invoices)
        if (invoiceCount > 0) {
          const { data: recentInvoices, error: invoicesError } = await supabase
            .from('invoices')
            .select('id')
            .eq('store_id', storeId)
            .eq('customer_phone', buyerPhone)
            .eq('is_manual_due', false)
            .eq('status', 'Completed')
            .order('creation_time', { ascending: false })
            .limit(20);

          if (invoicesError) {
            logger.error('Error fetching recent invoices', { error: invoicesError, component: 'buyerSlice' });
            throw invoicesError;
          }

          // Fetch items from these 20 invoices
          if (recentInvoices && recentInvoices.length > 0) {
            const recentInvoiceIds = recentInvoices.map((inv: any) => inv.id);

            const { data: invoiceItems, error: itemsError } = await supabase
              .from('invoice_items')
              .select('item_id')
              .in('invoice_id', recentInvoiceIds)
              .not('item_id', 'is', null);

            if (itemsError) {
              logger.error('Error fetching invoice items', { error: itemsError, component: 'buyerSlice' });
              throw itemsError;
            }

            // Get unique item IDs
            const boughtItemIds = new Set(
              (invoiceItems || [])
                .map((item: any) => item.item_id)
                .filter((id: string | null) => id !== null),
            );
            recentlyBoughtItemIds = Array.from(boughtItemIds);
          }
        }
      }

      return {
        storeId,
        store,
        items: safeItems,
        invoiceCount,
        totalDues,
        recentlyBoughtItemIds,
      };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch store details');
    }
  }
);

// Async thunk to place an order
export const placeBuyerOrder = createAsyncThunk(
  'buyer/placeOrder',
  async (
    {
      storeId,
      customerPhone,
      items,
      customerName,
      customerNameLocale,
      orderInstructions,
      deliveryAddress,
      deliveryAddressLocale,
    }: {
      storeId: string;
      customerPhone: string;
      items: OrderItem[];
      customerName?: string;
      customerNameLocale?: string;
      orderInstructions?: string | null;
      deliveryAddress?: string | null;
      deliveryAddressLocale?: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      // Validate inputs - allow order with either items OR instructions
      if (items.length === 0 && !orderInstructions?.trim()) {
        return rejectWithValue('Order must contain at least one item or order instructions');
      }
      
      const options: { customerName?: string; customerNameLocale?: string; orderInstructions?: string | null; deliveryAddress?: string | null; deliveryAddressLocale?: string | null } = {};
      if (customerName) {
        options.customerName = customerName;
      }
      if (customerNameLocale) {
        options.customerNameLocale = customerNameLocale;
      }
      if (orderInstructions) {
        options.orderInstructions = orderInstructions;
      }
      if (deliveryAddress) {
        options.deliveryAddress = deliveryAddress;
      }
      if (deliveryAddressLocale) {
        options.deliveryAddressLocale = deliveryAddressLocale;
      }
      const order = await CustomerOrderService.placeOrder(storeId, customerPhone, items, options);
      return order;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to place order');
    }
  }
);

const buyerSlice = createSlice({
  name: 'buyer',
  initialState,
  reducers: {
    // Set the currently active store in buyer flow
    setActiveStoreId(state, action: PayloadAction<string | null>) {
      state.activeStoreId = action.payload;
    },

    // Set explored stores (array of PublicStoreView)
    setExploredStores(state, action: PayloadAction<PublicStoreView[]>) {
      state.exploredStores = action.payload;
    },


    // ----- Order / Cart (per store) -----

    // Replace entire order for a store (e.g. hydrate from backend or restore)
    setBuyerStoreOrder(state, action: PayloadAction<BuyerStoreOrder>) {
      const order = action.payload;
      state.ordersByStoreId[order.storeId] = order;
    },

    // Add item to order
    addItemToBuyerStoreOrder(
      state,
      action: PayloadAction<{
        storeId: string;
        storeName: string;
        item: OrderItem;
      }>,
    ) {
      const { storeId, storeName, item } = action.payload;

      let order = state.ordersByStoreId[storeId];
      if (!order) {
        order = {
          storeId,
          storeName,
          items: [],
          orderInstructions: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const existingItemIndex = order.items.findIndex((i: OrderItem) => i.itemId === item.itemId);

      if (existingItemIndex >= 0) {
        const existingItem = order.items[existingItemIndex];
        if (existingItem) {
          existingItem.quantity += item.quantity;
          // Move updated item to front for recency
          order.items.splice(existingItemIndex, 1);
          order.items.unshift(existingItem);
        }
      } else {
        order.items.unshift(item);
      }

      order.updatedAt = new Date().toISOString();
      state.ordersByStoreId[storeId] = order;
    },

    updateBuyerStoreOrderItemQuantity(
      state,
      action: PayloadAction<{ storeId: string; itemId: string; quantity: number }>,
    ) {
      const { storeId, itemId, quantity } = action.payload;
      const order = state.ordersByStoreId[storeId];
      if (!order) return;

      const itemIndex = order.items.findIndex((i) => i.itemId === itemId);
      if (itemIndex < 0) return;

      if (quantity <= 0) {
        order.items.splice(itemIndex, 1);
      } else {
        const item = order.items[itemIndex];
        if (item) {
          item.quantity = quantity;
        }
      }

      order.updatedAt = new Date().toISOString();

      // If order becomes empty (no items AND no instructions), remove it
      if (order.items.length === 0 && !order.orderInstructions?.trim()) {
        delete state.ordersByStoreId[storeId];
      }
    },

    removeItemFromBuyerStoreOrder(
      state,
      action: PayloadAction<{ storeId: string; itemId: string }>,
    ) {
      const { storeId, itemId } = action.payload;
      const order = state.ordersByStoreId[storeId];
      if (!order) return;

      order.items = order.items.filter((i) => i.itemId !== itemId);
      order.updatedAt = new Date().toISOString();
      
      // If order becomes empty (no items AND no instructions), remove it
      if (order.items.length === 0 && !order.orderInstructions?.trim()) {
        delete state.ordersByStoreId[storeId];
      }
    },

    updateBuyerStoreOrderInstructions(
      state,
      action: PayloadAction<{ storeId: string; storeName: string; instructions: string | null }>,
    ) {
      const { storeId, storeName, instructions } = action.payload;
      let order = state.ordersByStoreId[storeId];
      if (!order) {
        // Create order if it doesn't exist (like when adding an item)
        order = {
          storeId,
          storeName,
          items: [],
          orderInstructions: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }
      order.orderInstructions = instructions;
      order.updatedAt = new Date().toISOString();
      state.ordersByStoreId[storeId] = order;
    },

    clearBuyerStoreOrder(state, action: PayloadAction<string>) {
      const storeId = action.payload;
      delete state.ordersByStoreId[storeId];
    },

    clearAllBuyerState(state) {
      state.activeStoreId = null;
      state.storesById = {};
      state.ordersByStoreId = {};
      state.exploredStores = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuyerConnectedStores.fulfilled, (state, action) => {
        const { stores } = action.payload;
        
        // Update exploredStores only
        state.exploredStores = stores;
      })
      .addCase(fetchBuyerConnectedStores.rejected, (_state, action) => {
        // Error handling - could add error state if needed
        logger.error('Failed to fetch connected stores', { error: action.payload, component: 'buyerSlice' });
      })
      .addCase(fetchBuyerStoreDetails.pending, (state, action) => {
        const { storeId } = action.meta.arg;
        const existing = state.storesById[storeId] ?? createEmptyStoreDetails();
        state.storesById[storeId] = {
          ...existing,
          isRefreshing: true,
          error: null,
        };
      })
      .addCase(fetchBuyerStoreDetails.fulfilled, (state, action) => {
        const { storeId, store, items, invoiceCount, totalDues, recentlyBoughtItemIds } = action.payload;
        state.storesById[storeId] = {
          meta: store,
          items,
          invoiceCount,
          totalDues,
          recentlyBoughtItemIds,
          isRefreshing: false,
          error: null,
          lastRefreshedAt: new Date().toISOString(),
        };
      })
      .addCase(fetchBuyerStoreDetails.rejected, (state, action) => {
        const { storeId } = action.meta.arg;
        const existing = state.storesById[storeId] ?? createEmptyStoreDetails();
        state.storesById[storeId] = {
          ...existing,
          isRefreshing: false,
          error: (action.payload as string) || 'Failed to fetch store details',
        };
      })
      .addCase(placeBuyerOrder.fulfilled, (state, action) => {
        const { storeId } = action.meta.arg;
        // Clear the cart after successful order placement
        delete state.ordersByStoreId[storeId];
      });
  },
});

export const {
  setActiveStoreId,
  setExploredStores,
  setBuyerStoreOrder,
  addItemToBuyerStoreOrder,
  updateBuyerStoreOrderItemQuantity,
  updateBuyerStoreOrderInstructions,
  removeItemFromBuyerStoreOrder,
  clearBuyerStoreOrder,
  clearAllBuyerState,
} = buyerSlice.actions;

export default buyerSlice.reducer;


